import type { PropsWithChildren } from 'react';
import { cn } from '@lqa-frontend/ui-system';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import * as React from 'react';
import { useResolvedPortalContainer } from '../../hooks/use-portal-container';
import { BodyS } from '../typography';

type TooltipSide = 'top' | 'bottom' | 'left' | 'right';

interface ITooltipContentProps extends React.ComponentProps<
	typeof TooltipPrimitive.Content
> {
	portalContainer?: HTMLElement | null;
}

interface ISimpleTooltipProps {
	content: string | React.ReactNode;
	triggerClassName?: string;
	contentClassName?: string;
	side?: TooltipSide;
}

function TooltipProvider({
	delayDuration = 0,
	...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
	return (
		<TooltipPrimitive.Provider
			data-slot="tooltip-provider"
			delayDuration={delayDuration}
			{...props}
		/>
	);
}

function Tooltip({
	...props
}: React.ComponentProps<typeof TooltipPrimitive.Root>) {
	return (
		<TooltipProvider>
			<TooltipPrimitive.Root data-slot="tooltip" {...props} />
		</TooltipProvider>
	);
}

function TooltipTrigger({
	...props
}: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
	return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />;
}

function TooltipContent({
	className,
	sideOffset = 0,
	children,
	portalContainer,
	...props
}: ITooltipContentProps) {
	const resolvedContainer = useResolvedPortalContainer(portalContainer);
	return (
		<TooltipPrimitive.Portal container={resolvedContainer}>
			<TooltipPrimitive.Content
				data-slot="tooltip-content"
				sideOffset={sideOffset}
				className={cn(
					'bg-text-primary text-white min-w-204 max-w-360 animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 w-fit rounded-m px-16 py-10 text-12 text-balance',
					className
				)}
				{...props}
			>
				<BodyS color="white">{children}</BodyS>
				<TooltipPrimitive.Arrow className="bg-text-primary fill-text-primary z-50 size-10 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-xxs" />
			</TooltipPrimitive.Content>
		</TooltipPrimitive.Portal>
	);
}

function SimpleTooltip({
	children,
	content,
	side = 'top',
	triggerClassName,
	contentClassName,
}: PropsWithChildren<ISimpleTooltipProps>) {
	return (
		<TooltipProvider>
			<Tooltip>
				<TooltipTrigger
					className={cn(
						'flex flex-row gap-4 items-center w-fit',
						triggerClassName
					)}
					asChild
				>
					<div>{children}</div>
				</TooltipTrigger>
				<TooltipContent side={side} className={contentClassName}>
					{content}
				</TooltipContent>
			</Tooltip>
		</TooltipProvider>
	);
}

export {
	Tooltip,
	TooltipTrigger,
	TooltipContent,
	TooltipProvider,
	SimpleTooltip,
};
export type { ISimpleTooltipProps, ITooltipContentProps, TooltipSide };
